import '../../styles/LandingPage/ResearchPillars/InnovationRisk.css';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function InnovationRisk() {
  const contentRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const items = contentRef.current?.querySelectorAll('.ir-animate');
    items?.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <section className="ir-detail-section" ref={contentRef}>
      <div className="ir-detail-container">
        <Link to="/" className="ir-back-link ir-animate">
          <i className="ti ti-arrow-left" />
          Back to Research Pillars
        </Link>

        <div className="ir-detail-header ir-animate">
          <div className="ir-detail-label">Pillar 05</div>
          <div className="ir-detail-icon">
            <i className="ti ti-cpu" />
          </div>
          <h1>Innovation & Emerging Risk</h1>
          <p className="ir-detail-subtitle">
            Climate actuarial modelling, parametric insurance, microinsurance sustainability, and AI in actuarial science.
          </p>
        </div>

        <div className="ir-content">
          <div className="ir-block ir-animate">
            <h2>Climate Actuarial Modelling</h2>
            <p>
              Climate change represents a systemic risk that transcends traditional actuarial boundaries. Our research 
              develops methodologies for quantifying climate-related financial risks in insurance portfolios, including 
              physical risks (floods, droughts, tropical cyclones) and transition risks (policy changes, technology disruption). 
              We examine how climate scenarios from the NGFS and IPCC can be translated into insurance-relevant metrics, 
              and how African insurers can incorporate climate risk into their pricing, reserving, and capital frameworks.
            </p>
            <div className="ir-highlight">
              <i className="ti ti-bulb" />
              <span>Key Insight: Agricultural insurance losses from climate events in Africa have increased 4x since 2000, yet penetration remains below 3%.</span>
            </div>
          </div>

          <div className="ir-block ir-animate">
            <h2>Parametric Insurance Design</h2>
            <p>
              Parametric insurance offers a promising solution for insuring risks where traditional indemnity products 
              are uneconomical. Our research designs parametric triggers for African agricultural, livestock, and disaster risks, 
              examining the trade-offs between basis risk and administrative efficiency. We develop satellite-based index 
              products, weather station triggers, and hybrid models that combine multiple data sources to minimise basis risk 
              while maintaining product simplicity and affordability.
            </p>
          </div>

          <div className="ir-block ir-animate">
            <h2>Microinsurance Sustainability</h2>
            <p>
              Microinsurance has the potential to extend financial protection to millions of underserved Africans, 
              but sustainability remains a challenge. Our research examines the economics of microinsurance distribution, 
              including agent networks, mobile platforms, and group schemes. We analyse the impact of premium subsidies, 
              reinsurance support, and technology on the viability of microinsurance programmes, and develop 
              actuarial pricing models that balance affordability with technical adequacy.
            </p>
          </div>

          <div className="ir-block ir-animate">
            <h2>AI in Actuarial Science</h2>
            <p>
              Artificial intelligence and machine learning are transforming actuarial practice. Our research explores 
              the application of advanced analytics to actuarial problems — from predictive modelling of claims 
              and lapse behaviour to automated underwriting and fraud detection. We examine the governance challenges 
              of AI in insurance, including model interpretability, bias detection, and regulatory compliance, 
              and develop frameworks for responsible AI deployment in African insurance markets.
            </p>
          </div>

          <div className="ir-block ir-animate">
            <h2>Current Research Outputs</h2>
            <div className="ir-outputs">
              <div className="ir-output-card">
                <i className="ti ti-file-analytics" />
                <h4>Climate Risk Integration Framework</h4>
                <span>Technical Paper · 2025</span>
              </div>
              <div className="ir-output-card">
                <i className="ti ti-droplet" />
                <h4>Parametric Drought Index Model</h4>
                <span>Open-source Tool · 2025</span>
              </div>
              <div className="ir-output-card">
                <i className="ti ti-brain" />
                <h4>AI Governance for African Insurers</h4>
                <span>Policy Brief · 2025</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}